import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  Typography
} from '@mui/material';

const sections = [
  {
    title: 'Introduction',
    body: 'Use this API to authenticate client portal users, create background checks, import bulk requests, retrieve check status, and manage team-level access.'
  },
  {
    title: 'Definitions',
    body: 'Responses are documented in OpenAPI 3.1.1. Authentication uses JWT Bearer tokens. Final screening outcomes follow the values `clear` and `not_clear` when a completed result exists.'
  },
  {
    title: 'Specifications',
    body: 'The canonical workflow is login, authorize, create or import background checks, and then poll the status endpoint until the check reaches a terminal state.'
  },
  {
    title: 'Security Considerations',
    body: 'Never expose your bearer token in client-side logs or public URLs. Rotate compromised tokens immediately and scope client credentials to the minimum required access.'
  }
];

const DocsLandingPage: React.FC = () => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #081224 0%, #0f172a 35%, #f8fafc 35%, #f8fafc 100%)',
        py: { xs: 4, md: 6 }
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={4}>
          <Box sx={{ color: '#fff', pt: { xs: 2, md: 4 } }}>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 700, mb: 1.5 }}>
              Valida.do API Documentation
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: 820, color: 'rgba(255,255,255,0.82)', mb: 3 }}>
              Interactive API reference for the client portal, aligned with OpenAPI 3.1.1 and focused on a clean
              developer onboarding flow.
            </Typography>
          </Box>

          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 24px 80px rgba(15, 23, 42, 0.16)'
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Stack divider={<Divider flexItem />} spacing={3}>
                {sections.map((section) => (
                  <Box key={section.title}>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                      {section.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {section.body}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 24px 80px rgba(15, 23, 42, 0.16)'
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: '#fff'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                API Reference
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Interactive Swagger UI rendered from the live OpenAPI specification.
              </Typography>
            </Box>
            <Box sx={{ bgcolor: '#fff', height: { xs: '75vh', lg: '82vh' } }}>
              <Box
                component="iframe"
                title="Valida.do Swagger UI"
                src="/api/docs/client-portal"
                sx={{
                  width: '100%',
                  height: '100%',
                  border: 0,
                  display: 'block'
                }}
              />
            </Box>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default DocsLandingPage;
